import React, { useState, useEffect } from "react";
import "./InventoryList.css";
import { getInventory, getLowStockItems } from "../../services/inventoryService";
import { getProducts } from "../../services/productService";
import { AiOutlineEdit, AiOutlineDelete, AiOutlineWarning, AiOutlinePlus, AiOutlineDatabase, AiOutlineDollarCircle, AiOutlineClockCircle } from "react-icons/ai";
import InventoryRegistrationModal from "./InventoryRegistrationModal";
import Popup from "../../components/ui/Popup";

const Inventory = () => {
    const [inventory, setInventory] = useState([]);
    const [products, setProducts] = useState([]);
    const [lowStockItems, setLowStockItems] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [filterStock, setFilterStock] = useState("");
    const [totalInventoryValue, setTotalInventoryValue] = useState(0);
    const [recentUpdates, setRecentUpdates] = useState(0);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [popupType, setPopupType] = useState("");
    const [popupMessage, setPopupMessage] = useState("");

    // Pagination States
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 5;

    useEffect(() => {
        fetchInventory();
        fetchLowStockItems();
        fetchProducts();
    }, []);

    useEffect(() => {
        if (inventory.length > 0 && products.length > 0) {
            calculateInventoryValue();
        }
    }, [inventory, products]);

    // Reset to first page when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [searchQuery, filterStock]);

    const fetchInventory = async () => {
        try {
            const response = await getInventory();
            setInventory(response.data.reverse()); // ✅ New items appear on top
        } catch (error) {
            console.error("Failed to fetch inventory:", error);
        }
    };

    const fetchLowStockItems = async () => {
        try {
            const response = await getInventory(); // Fetch full inventory
            const lowStock = response.data.filter(item => item.quantity <= item.reorder_level); // ✅ Filter low stock items
            setLowStockItems(lowStock); // ✅ Update state
        } catch (error) {
            console.error("Failed to fetch low stock items:", error);
        }
    };

    const fetchProducts = async () => {
        try {
            const response = await getProducts();
            if (Array.isArray(response)) {
                setProducts(response);
            } else if (response && response.data && Array.isArray(response.data)) {
                setProducts(response.data);
            } else {
                console.error("Invalid product data format", response);
                setProducts([]);
            }
        } catch (error) {
            console.error("Failed to fetch products:", error);
            setProducts([]);
        }
    };

    const calculateInventoryValue = () => {
        if (!inventory.length || !products.length) {
            setTotalInventoryValue(0);
            return;
        }

        let totalValue = inventory.reduce((sum, item) => {
            const product = products.find(p => Number(p.id) === Number(item.product_id));
            const unitPrice = product ? parseFloat(product.price) || 0 : 0;
            return sum + (item.quantity * unitPrice);
        }, 0);

        setTotalInventoryValue(totalValue);
    };

    const [selectedItem, setSelectedItem] = useState(null); // Store selected item for editing

    const handleEdit = (item) => {
        console.log("Editing:", item);
        setSelectedItem(item); // Store the item in state
        setIsModalOpen(true);
    };

    const handleDelete = async (item) => {
        if (!window.confirm(`Are you sure you want to delete ${item.product_name}?`)) return;
        try {
            setInventory(inventory.filter(inv => inv.id !== item.id));
            console.log("Deleted successfully!");
        } catch (error) {
            console.error("Error deleting item:", error);
        }
    };

    // Apply filters to inventory data
    const filteredInventory = inventory.filter(item => {
        // Search query filter (case insensitive)
        const matchesSearch = !searchQuery ||
            item.product_name.toLowerCase().includes(searchQuery.toLowerCase());

        // Stock status filter
        let matchesStockFilter = true;
        if (filterStock === "Low") {
            matchesStockFilter = item.quantity <= item.reorder_level;
        } else if (filterStock === "Available") {
            matchesStockFilter = item.quantity > item.reorder_level;
        }

        return matchesSearch && matchesStockFilter;
    });

    // ✅ Pagination Logic
    const totalPages = Math.ceil(filteredInventory.length / itemsPerPage);
    const paginatedInventory = filteredInventory.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages) {
            setCurrentPage(newPage);
        }
    };

    return (
        <div className="content-wrapper">
            <Popup message={popupMessage} type={popupType} onClose={() => setPopupMessage("")} />
            <div className="statistics">
                <div className="stat-tile"><AiOutlineDatabase /> Total Products: {inventory.length}</div>
                <div className="stat-tile"><AiOutlineWarning /> Low Stock: {lowStockItems.length}</div>
                <div className="stat-tile"><AiOutlineDollarCircle /> Inventory Value: Rs. {totalInventoryValue.toFixed(2)}</div>
                {/*<div className="stat-tile"><AiOutlineClockCircle /> Recently Updated: {recentUpdates} items</div>*/}
            </div>

            <div className="filters">
                <input
                    type="text"
                    placeholder="Search Product..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                />
                <select
                    value={filterStock}
                    onChange={(e) => setFilterStock(e.target.value)}
                >
                    <option value="">All Stock</option>
                    <option value="Low">Low Stock</option>
                    <option value="Available">Available Stock</option>
                </select>
                <button className="add-gun-btn" onClick={() => setIsModalOpen(true)}>
                    <AiOutlinePlus /> Add New Inventory
                </button>
            </div>

            <div className="table-wrapper">
                <table>
                    <thead>
                    <tr>
                        <th>Product Name</th>
                        <th>Stock Quantity</th>
                        <th>Reorder Level</th>
                        <th>Unit Price</th>
                        <th>Total Value</th>
                        <th>Actions</th>
                    </tr>
                    </thead>
                    <tbody>
                    {paginatedInventory.length > 0 ? (
                        paginatedInventory.map((item) => {
                            const product = products.find(p => p.id === item.product_id);
                            const unitPrice = product ? parseFloat(product.price) || 0 : 0;
                            const totalValue = (item.quantity * unitPrice).toFixed(2);

                            return (
                                <tr key={item.id}>
                                    <td>{item.product_name}</td>
                                    <td className={item.quantity <= item.reorder_level ? "status-cell low-stock" : "status-cell"}>{item.quantity}</td>
                                    <td>{item.reorder_level}</td>
                                    <td>Rs. {unitPrice.toFixed(2)}</td>
                                    <td>Rs. {totalValue}</td>
                                    <td className="actions">
                                        <div className="action-buttons">
                                            <button className="edit-btn" onClick={() => handleEdit(item)}>Edit</button>
                                        </div>
                                    </td>
                                </tr>
                            );
                        })
                    ) : (
                        <tr>
                            <td colSpan="6" className="no-results">No items match your search criteria</td>
                        </tr>
                    )}
                    </tbody>
                </table>
            </div>

            {/* ✅ Pagination Controls */}
            <div className="pagination">
                <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>← Previous</button>
                <span> Page {currentPage} of {Math.max(1, totalPages)} </span>
                <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages || totalPages === 0}>Next →</button>
            </div>

            {isModalOpen && (
                <InventoryRegistrationModal
                    onClose={() => {
                        setIsModalOpen(false);
                        setSelectedItem(null); // Clear after closing
                    }}
                    selectedItem={selectedItem} // Pass selected item to modal
                    onInventoryUpdated={() => {
                        fetchInventory();
                        fetchLowStockItems();
                        setPopupMessage("Inventory updated successfully!");
                        setPopupType("success");
                    }}
                    onInventoryAdded={() => {
                        fetchInventory();
                        fetchLowStockItems();
                        setPopupMessage("Inventory item added successfully!");
                        setPopupType("success");
                    }}
                />
            )}
        </div>
    );
};

export default Inventory;